/**
 * #14194: recover from a stale lazy chunk after a deploy.
 *
 * A tab opened before a deploy still holds the old asset manifest. When it opens a lazy page, the
 * browser asks for a chunk file the deploy removed, and the import fails with "Failed to fetch
 * dynamically imported module". A full reload fetches the new manifest and the page loads.
 *
 * `reloadOnceForChunkError` does that reload at most once: a sessionStorage stamp marks the
 * reload, and a second chunk error inside RELOAD_GUARD_MS does not reload again, so a chunk that
 * is truly missing shows the error card instead of a reload loop. A storage read or write that
 * fails counts as "already reloaded" (fail safe: no loop).
 */

/** The sessionStorage key that stamps the last automatic chunk reload (epoch ms). */
export const CHUNK_RELOAD_KEY = 'sbs:chunk-reload-at';

/** Within this window after an automatic reload, a second chunk error does not reload again. */
export const RELOAD_GUARD_MS = 60_000;

/** The messages each browser gives for a failed dynamic import or a missing chunk. */
const CHUNK_ERROR_RE =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Loading chunk [\w-]+ failed|Unable to preload CSS/i;

/** True for a failed lazy import (a stale or missing chunk). Pure. */
export function isChunkLoadError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const e = error as { name?: unknown; message?: unknown };
  if (e.name === 'ChunkLoadError') return true;
  return typeof e.message === 'string' && CHUNK_ERROR_RE.test(e.message);
}

/** The storage this module reads: the sessionStorage subset it needs. */
export interface ReloadStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/**
 * True when an automatic reload may run now, and stamps it; false inside the guard window or when
 * storage fails. Pure apart from the stamp it writes.
 */
export function claimChunkReload(storage: ReloadStorage | null, now: number): boolean {
  if (!storage) return false;
  try {
    const last = Number(storage.getItem(CHUNK_RELOAD_KEY));
    if (Number.isFinite(last) && last > 0 && now - last < RELOAD_GUARD_MS) return false;
    storage.setItem(CHUNK_RELOAD_KEY, String(now));
    return true;
  } catch {
    return false;
  }
}

function sessionStore(): ReloadStorage | null {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

export interface ChunkReloadDeps {
  storage?: ReloadStorage | null;
  reload?: () => void;
  now?: number;
}

/**
 * On a chunk-load error, reload the page once (guarded). Returns true when it started a reload,
 * false when the error is not a chunk error or the guard holds.
 */
export function reloadOnceForChunkError(error: unknown, deps: ChunkReloadDeps = {}): boolean {
  if (!isChunkLoadError(error)) return false;
  const storage = deps.storage === undefined ? sessionStore() : deps.storage;
  if (!claimChunkReload(storage, deps.now ?? Date.now())) return false;
  (deps.reload ?? (() => window.location.reload()))();
  return true;
}

/**
 * Wrap a lazy loader: a chunk-load error reloads the page once and leaves the import pending, so
 * React keeps the Suspense fallback up while the page reloads. Any other error, or a chunk error
 * inside the guard window, rethrows to the error boundary.
 */
export function withChunkReload<T>(loader: () => Promise<T>, deps: ChunkReloadDeps = {}): () => Promise<T> {
  return async () => {
    try {
      return await loader();
    } catch (error) {
      if (reloadOnceForChunkError(error, deps)) return new Promise<T>(() => undefined);
      throw error;
    }
  };
}
